/**
 * AdaptyEventReviewQueue.tsx — webhook 事件人工闭环队列（ADM-FIN-S010，财务页「事件闭环」tab）
 *
 * 接真：GET /api/v1/finance/adapty-event-reviews（useAdaptyEventReviews，offset + App scope 注入 app_id）。
 * 队列 = 不再重放、也不会自愈的 Adapty webhook 事件台账（默认 resolution_status=OPEN）。
 * 运营核对行内快照后经 AcknowledgeEventReviewDialog 确认结案（不触发重放）。
 * 列表只读行内快照，不反查源表 / R2，不透传 payload。
 */

import type {
  AdaptyEventReviewAdmissionKind,
  AdaptyEventReviewErrorCode,
  AdaptyEventReviewResolutionStatus,
} from '@novel-admin/shared';
import {
  ADAPTY_EVENT_REVIEW_ADMISSION_KINDS,
  ADAPTY_EVENT_REVIEW_ERROR_CODES,
} from '@novel-admin/shared';
import { useEffect, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { type EventReviewQueueRow, useAdaptyEventReviews } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { ContextMenu, type ContextMenuItem } from '~/ui/ContextMenu';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Select } from '~/ui/Select';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import {
  ADMISSION_KIND_ZH,
  ERROR_CODE_ZH,
  RESOLUTION_STATUS_ZH,
} from './adapty-event-review-labels';
import { AcknowledgeEventReviewDialog } from './dialogs/AcknowledgeEventReviewDialog';

export function AdaptyEventReviewQueue() {
  const appId = useAppScope((s) => s.appId);
  const pageSize = useAppScope((s) => s.tablePageSize);
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = role === 'root' || role === 'admin' || role === 'operator';
  const [status, setStatus] = useState<AdaptyEventReviewResolutionStatus>('OPEN');
  const [admissionKind, setAdmissionKind] = useState<AdaptyEventReviewAdmissionKind | ''>('');
  const [errorCode, setErrorCode] = useState<AdaptyEventReviewErrorCode | ''>('');
  const [page, setPage] = useState(1);
  const {
    data: rows,
    total,
    isLoading,
    isError,
  } = useAdaptyEventReviews({
    resolutionStatus: status,
    admissionKind: admissionKind || undefined,
    errorCode: errorCode || undefined,
    page,
    limit: pageSize,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: 筛选 / scope / 页档变化时重置页码
  useEffect(() => {
    setPage(1);
  }, [appId, status, admissionKind, errorCode, pageSize]);
  useEffect(() => {
    if (isLoading) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [total, pageSize, page, isLoading]);

  const [q, setQ] = useState('');
  const [target, setTarget] = useState<EventReviewQueueRow | null>(null);

  const shown = rows.filter((r) => {
    if (!q) return true;
    const k = q.toLowerCase();
    return (
      r.adapty_event_id.toLowerCase().includes(k) ||
      r.event_type.toLowerCase().includes(k) ||
      (r.customer_user_id ?? '').toLowerCase().includes(k) ||
      (r.vendor_product_id ?? '').toLowerCase().includes(k) ||
      (r.adapty_profile_id ?? '').toLowerCase().includes(k)
    );
  });

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <SearchInput
          width={280}
          placeholder="事件 ID / 类型 / 用户标识 / 产品"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as AdaptyEventReviewResolutionStatus)}
          style={{ width: 120 }}
        >
          {(Object.keys(RESOLUTION_STATUS_ZH) as AdaptyEventReviewResolutionStatus[]).map((s) => (
            <option key={s} value={s}>
              {RESOLUTION_STATUS_ZH[s]}
            </option>
          ))}
        </Select>
        <Select
          value={admissionKind}
          onChange={(e) => setAdmissionKind(e.target.value as AdaptyEventReviewAdmissionKind | '')}
          style={{ width: 130 }}
        >
          <option value="">全部准入</option>
          {ADAPTY_EVENT_REVIEW_ADMISSION_KINDS.map((k) => (
            <option key={k} value={k}>
              {ADMISSION_KIND_ZH[k]}
            </option>
          ))}
        </Select>
        <Select
          value={errorCode}
          onChange={(e) => setErrorCode(e.target.value as AdaptyEventReviewErrorCode | '')}
          style={{ width: 170 }}
        >
          <option value="">全部错误码</option>
          {ADAPTY_EVENT_REVIEW_ERROR_CODES.map((c) => (
            <option key={c} value={c}>
              {ERROR_CODE_ZH[c] ?? c}
            </option>
          ))}
        </Select>
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          共 {total} 条（本页筛 {shown.length}）
        </span>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th>事件 ID</th>
              <th>App</th>
              <th>类型</th>
              <th>错误码</th>
              <th>用户标识</th>
              <th>产品</th>
              <th>状态</th>
              <th>进台账</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const items: ContextMenuItem[] = [
                {
                  label: '查看快照',
                  icon: 'IconSearch',
                  onClick: () => setTarget(r),
                },
                ...(canWrite && r.resolution_status === 'OPEN'
                  ? [
                      {
                        label: '确认闭环',
                        icon: 'IconCheck',
                        onClick: () => setTarget(r),
                      } as const,
                    ]
                  : []),
                {
                  label: '复制事件 ID',
                  icon: 'IconCopy',
                  onClick: () => {
                    navigator.clipboard?.writeText(r.adapty_event_id);
                    toast(`已复制 ${r.adapty_event_id}`, { tone: 'pos', icon: 'IconCopy' });
                  },
                },
              ];
              return (
                <tr key={`${r.app_id}:${r.adapty_event_id}`}>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.adapty_event_id}
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {r.appName}
                    </span>
                  </td>
                  <td
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--fg-muted)' }}
                    title={r.event_type}
                  >
                    {r.event_type}
                  </td>
                  <td>
                    <span
                      className={`chip ${r.admission_kind === 'TERMINAL' ? 'chip-neg' : 'chip-warn'}`}
                      style={{ fontSize: 10 }}
                      title={r.error_code}
                    >
                      {ERROR_CODE_ZH[r.error_code] ?? r.error_code}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11 }} title="源行原值，可能非法">
                    {r.customer_user_id ?? <span className="muted">—</span>}
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {r.vendor_product_id ?? <span className="muted">—</span>}
                  </td>
                  <td>
                    <span
                      className={`chip ${r.resolution_status === 'ACKNOWLEDGED' ? 'chip-pos' : 'chip-warn'}`}
                      style={{ fontSize: 10 }}
                    >
                      {RESOLUTION_STATUS_ZH[r.resolution_status]}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                    {fmtDateTime(r.admitted_at)}
                  </td>
                  <td>
                    <ContextMenu items={items}>
                      <button className="btn btn-ghost btn-icon" style={{ width: 22, height: 22 }}>
                        <Icons.IconMoreV size={12} />
                      </button>
                    </ContextMenu>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && !isLoading && (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    textAlign: 'center',
                    padding: 32,
                    color: isError ? 'var(--neg)' : 'var(--fg-muted)',
                    fontSize: 12,
                  }}
                >
                  {isError ? '队列加载失败，请刷新重试' : q ? '无匹配的事件' : '暂无待闭环事件'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={setPage}
          totalMode="exact"
        />
      </div>

      {target && (
        <AcknowledgeEventReviewDialog
          row={target}
          canWrite={canWrite}
          onClose={() => setTarget(null)}
        />
      )}
    </div>
  );
}
